import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatTimecode, formatTimeOnly, ariaTimeText } from '../utils/timecodeFormatter';
import { getMarkerFillUri, getMarkerClipPath } from '../utils/markerSvg';
import type { CommentMarker } from '../types';

interface SeekBarProps {
  currentTime: number;
  duration: number;
  bufferedEnd: number;
  mediaElementId: string;
  onSeekStart: () => void;
  onSeekEnd: (time: number) => void;
  fps?: number;
  startFrame?: number;
  dropFrame?: boolean;
  thumbnailSpriteUrl?: string;
  comments?: CommentMarker[];
  onMarkerClick?: (time: number, commentId: number) => void;
  /** Callback ref to expose the seek bar element for external measurement. */
  barRefCallback?: (el: HTMLDivElement | null) => void;
  /** Called when the mouse enters or leaves the seek bar. */
  onHoverChange?: (hovering: boolean) => void;
}

export function SeekBar({
  currentTime,
  duration,
  bufferedEnd,
  mediaElementId,
  onSeekStart,
  onSeekEnd,
  fps,
  startFrame = 0,
  dropFrame = false,
  thumbnailSpriteUrl,
  comments,
  onMarkerClick,
  barRefCallback,
  onHoverChange,
}: SeekBarProps) {
  const ROUNDED_MARKERS = true;

  const isDraggingRef = useRef(false);
  const skipNextChangeRef = useRef(false);
  const barRef = useRef<HTMLDivElement>(null);
  const clipId = useId();

  // Hover state
  const [hoverActive, setHoverActive] = useState(false);
  const [hoverX, setHoverX] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<number | null>(null);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferPercent = duration > 0 ? (bufferedEnd / duration) * 100 : 0;

  // Filter markers (same logic as old MarkerBar)
  const markers = useMemo(() => {
    if (duration <= 0 || !comments || comments.length === 0) return [];

    const filtered: CommentMarker[] = [];
    let lastPos = -1;

    for (const comment of comments) {
      // Skip replies
      if (comment.reply > 0) continue;

      const pos = comment.pos;
      // Skip if NaN
      if (isNaN(pos)) continue;

      // Skip comments too close together (< 1% of duration)
      if (lastPos >= 0 && pos - lastPos < duration * 0.01) continue;

      lastPos = pos;
      filtered.push(comment);
    }

    return filtered;
  }, [comments, duration]);

  // Thumb half-widths for alignment: the range input thumb center at value 0%
  // sits thumbHalf px from the left edge, not at 0px.  We need both sizes
  // because the CSS thumb expands on hover and the browser repositions the
  // track accordingly.  Markers get both positions via CSS custom properties
  // (--mc / --mc-hover) so CSS transitions keep `left` in sync with the
  // width/height expansion.
  const THUMB_HALF = 6;        // Non-hover: 8px + 2×2px border = 12px → half = 6
  const THUMB_HALF_HOVER = 9;  // Hover: 14px + 2×2px border = 18px → half = 9

  // Find the marker closest to a given clientX, within a pixel threshold.
  // Uses THUMB_HALF_HOVER because clicks/hovers only happen when the bar
  // is in hover state and the thumb has expanded.
  const findMarkerAtX = useCallback(
    (clientX: number): CommentMarker | null => {
      if (!barRef.current || markers.length === 0 || duration <= 0) return null;
      const rect = barRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const effective = rect.width - 2 * THUMB_HALF_HOVER;
      const threshold = 8; // px

      for (const marker of markers) {
        const markerCenter = THUMB_HALF_HOVER + (marker.pos / duration) * effective;
        if (Math.abs(clickX - markerCenter) <= threshold) {
          return marker;
        }
      }
      return null;
    },
    [markers, duration],
  );

  const handleInput = useCallback(
    () => {
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        onSeekStart();
      }
    },
    [onSeekStart],
  );

  // Detect marker clicks on mousedown — this fires BEFORE onChange,
  // so we can set skipNextChangeRef to prevent onChange from triggering
  // a redundant (and duplicate-dialog-producing) onSeekEnd call.
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      if (findMarkerAtX(e.clientX) && onMarkerClick) {
        skipNextChangeRef.current = true;
      }
    },
    [findMarkerAtX, onMarkerClick],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (skipNextChangeRef.current) {
        skipNextChangeRef.current = false;
        return;
      }
      const time = parseFloat(e.target.value);
      isDraggingRef.current = false;
      onSeekEnd(time);
    },
    [onSeekEnd],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      // Only handle marker clicks — regular seeks are handled by onChange
      const marker = findMarkerAtX(e.clientX);
      if (marker && onMarkerClick) {
        skipNextChangeRef.current = true;
        isDraggingRef.current = false;
        onMarkerClick(marker.pos, marker.id);
      }
      // Release focus so Space toggles play/pause instead of moving the slider
      (e.target as HTMLInputElement).blur();
    },
    [findMarkerAtX, onMarkerClick],
  );

  // Hover handlers on the outer seek-bar div
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!barRef.current || duration <= 0) return;
      const rect = barRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      // Compute hover time using the hover-state thumb half (tooltip only
      // shows when hovering, so the expanded thumb size applies)
      const eff = rect.width - 2 * THUMB_HALF_HOVER;
      const time = eff > 0
        ? Math.max(0, Math.min(duration, ((x - THUMB_HALF_HOVER) / eff) * duration))
        : 0;
      setHoverX(x);
      setHoverTime(time);
      setHoverActive(true);

      // Detect marker hover
      const marker = findMarkerAtX(e.clientX);
      setHoveredMarkerId(marker ? marker.id : null);
    },
    [duration, findMarkerAtX],
  );

  const handleMouseEnter = useCallback(() => {
    setHoverActive(true);
    onHoverChange?.(true);
  }, [onHoverChange]);

  const handleMouseLeave = useCallback(() => {
    setHoverActive(false);
    setHoveredMarkerId(null);
    onHoverChange?.(false);
  }, [onHoverChange]);

  // Tooltip timecode — hoverTime is computed in handleMouseMove using the
  // same thumbHalf-adjusted coordinate system as the range input and markers
  const hoverTimecode = fps
    ? formatTimecode(hoverTime, fps, startFrame, dropFrame)
    : formatTimeOnly(hoverTime);

  // Compute thumbnail frame index (0–99, matching the sprite strip convention)
  const hoverFraction = duration > 0 ? hoverTime / duration : 0;
  const hoverFrame = Math.max(0, Math.min(99, Math.ceil(hoverFraction * 100) - 1));

  // Track bar width via ResizeObserver so marker positions update on resize
  const [barWidth, setBarWidth] = useState(0);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    setBarWidth(el.offsetWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(() => setBarWidth(el.offsetWidth));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Clamp tooltip position so it doesn't overflow edges of the bar
  const tooltipHalfWidth = thumbnailSpriteUrl ? 64 : 40;
  const clampedX = barWidth > 0
    ? Math.max(tooltipHalfWidth, Math.min(hoverX, barWidth - tooltipHalfWidth))
    : hoverX;

  return (
    <div
      ref={(el) => {
        (barRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        barRefCallback?.(el);
      }}
      className="kollab-player-seek-bar"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="kollab-player-seek-track" aria-hidden="true" />
      <div
        className="kollab-player-seek-buffer"
        style={{ width: `${bufferPercent}%` }}
        aria-hidden="true"
      />
      <div
        className="kollab-player-seek-progress"
        style={{ width: `${progressPercent}%` }}
        aria-hidden="true"
      />

      {/* Shared clip-path definition for marker border shape */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d={getMarkerClipPath(ROUNDED_MARKERS)} />
          </clipPath>
        </defs>
      </svg>

      {/* Comment markers — outer positions + shadow, shape layer clips, fill has SVG */}
      {barWidth > 0 && markers.map((marker) => {
        const isHovered = hoveredMarkerId === marker.id;
        // Two center positions: non-hover and hover.  CSS transitions
        // animate `left` between --mc and --mc-hover in sync with width.
        const effSm = barWidth - 2 * THUMB_HALF;
        const effLg = barWidth - 2 * THUMB_HALF_HOVER;
        const centerSm = THUMB_HALF + (marker.pos / duration) * effSm;
        const centerLg = THUMB_HALF_HOVER + (marker.pos / duration) * effLg;

        return (
          <div
            key={marker.id}
            className="kollab-player-seek-marker"
            style={{
              '--mc': `${centerSm}px`,
              '--mc-hover': `${centerLg}px`,
              transform: 'translateX(-50%)',
            } as React.CSSProperties}
            data-id={marker.id}
            role="button"
            tabIndex={-1}
            aria-label={`Comment at ${Math.floor(marker.pos)} seconds`}
          >
            <div
              className="kollab-player-seek-marker-shape"
              style={{
                clipPath: `url(#${clipId})`,
                backgroundColor: isHovered ? '#fff' : 'transparent',
              }}
            >
              <div
                className="kollab-player-seek-marker-fill"
                style={{ backgroundImage: getMarkerFillUri(marker.color, 13, 15, ROUNDED_MARKERS) }}
              />
            </div>
          </div>
        );
      })}

      <input
        type="range"
        className="kollab-player-seek-slider"
        min={0}
        max={duration || 0}
        step={0.1}
        value={currentTime}
        onMouseDown={handleMouseDown}
        onInput={handleInput}
        onChange={handleChange}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp as unknown as React.TouchEventHandler<HTMLInputElement>}
        aria-label="Seek"
        aria-controls={mediaElementId}
        aria-valuenow={Math.floor(currentTime)}
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration)}
        aria-valuetext={ariaTimeText(currentTime)}
      />

      {/* Hover tooltip */}
      {hoverActive && duration > 0 && (
        <div
          className="kollab-player-seek-tooltip"
          style={{
            left: `${clampedX}px`,
            transform: 'translateX(-50%)',
          }}
          aria-hidden="true"
        >
          {thumbnailSpriteUrl && React.createElement('thumbnail-image', {
            className: 'kollab-player-seek-tooltip-thumb',
            'hover-src': thumbnailSpriteUrl,
            'hover-frame': String(hoverFrame),
            'no-scrub': 'true',
          })}
          <div className="kollab-player-seek-tooltip-tc">{hoverTimecode}</div>
        </div>
      )}
    </div>
  );
}
